import { Cuboid } from '@App/app/entities/layer/cuboid.model';
import { LAYER_NAMES } from '@App/app/entities/layer/enums/layer-names.enum';
import { LAYER_TYPES } from '@App/app/entities/layer/enums/layer-types.enum';
import { LayerUI } from '@App/app/entities/layer/layer-ui.model';
import { RectTube } from '@App/app/entities/layer/rect-tube.model';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Axis, Matrix, Mesh, Plane, Quaternion, Tools, Vector3 } from 'babylonjs';
import { addLayer } from 'src/app/pages/viewer/store/actions/layers.actions';
import { BabylonNodesService } from '../../../babylon-nodes-service/babylon-nodes.service';
import { UtilsService } from '../../../utils-service/utils.service';
import { BasePlaneService } from '../../base-plane-services/base-plane.service';
import { RectangleToolsEdgeDetectService } from './rectangle-tools-edge-detect.service';
import { RectangleToolsUtilsService } from './rectangle-tools-utls.service';
import { VolumeUtilsService } from './volume-utils.service';

@Injectable({
  providedIn: 'root',
})
export class RectangleToolsSaveService {
  constructor(
    private basePlaneService: BasePlaneService,
    private rectangleToolsUtilsService: RectangleToolsUtilsService,
    // eslint-disable-next-line ngrx/use-consistent-global-store-name
    private store: Store,
    private utilsService: UtilsService,
    private volumeUtilsService: VolumeUtilsService,
    private babylonNodesService: BabylonNodesService,
    private rectangleToolsEdgeDetectService: RectangleToolsEdgeDetectService,
  ) {}

  save(
    type: LAYER_TYPES.CUBOID | LAYER_TYPES.RECT_TUBE,
    topPlaneNormal: Vector3,
    surfacePlaneNormal: Vector3,
    center: Vector3,
    width: number,
    depth: number,
    height: number,
    sampleMeshes: Mesh[],
    thickness?: number,
  ) {
    const options = { height, depth, width };

    const tmpBox = this.createOrientedBox(center, options, topPlaneNormal, surfacePlaneNormal);
    const [
      updatedCenter,
      updatedHeight,
    ] = this.rectangleToolsEdgeDetectService.detectEdgesAndRecalculateHeight(
      tmpBox,
      sampleMeshes,
      type,
    );
    options.height = updatedHeight;
    tmpBox.dispose();

    const box = this.createOrientedBox(updatedCenter, options, topPlaneNormal, surfacePlaneNormal);
    box.visibility = 0;

    const layer = this.buildLayerFromBox(type, box, thickness);
    box.dispose();
    this.store.dispatch(addLayer({ layer: layer as LayerUI, commit: false }));
  }

  calculateAzimuthAndVerticalTilt(
    mesh: Mesh,
    modelCenter: Vector3 = this.basePlaneService.getBasePlanesPosition(),
  ): [number, number] {
    mesh.bakeCurrentTransformIntoVertices(true);
    const vertices = this.utilsService.getVectorVertices(mesh);
    const frontAndBackPlanes = this.volumeUtilsService.findPlanesTwoLargestSides(vertices);

    const distanceToPlane = (plane: Plane) =>
      Vector3.Distance(
        modelCenter.projectOnPlane(plane, modelCenter.add(plane.normal)),
        modelCenter,
      );
    const planeToCalculate =
      distanceToPlane(frontAndBackPlanes[0]) > distanceToPlane(frontAndBackPlanes[1])
        ? frontAndBackPlanes[0]
        : frontAndBackPlanes[1];

    const targetCenter = modelCenter.projectOnPlane(
      planeToCalculate,
      modelCenter.add(planeToCalculate.normal),
    );
    const isFacingAway =
      Vector3.Distance(targetCenter.add(planeToCalculate.normal), modelCenter) >
      Vector3.Distance(targetCenter.subtract(planeToCalculate.normal), modelCenter);
    const normal = isFacingAway ? planeToCalculate.normal : planeToCalculate.normal.negate();

    const azimuth = this.volumeUtilsService.calculateAzimuth(normal);
    const verticalTilt = this.volumeUtilsService.calculateVerticalTilt(normal);
    return [azimuth, verticalTilt];
  }

  private createOrientedBox(
    center: Vector3,
    options: { height: number; depth: number; width: number },
    topPlaneNormal: Vector3,
    surfacePlaneNormal: Vector3,
  ): Mesh {
    const box = this.babylonNodesService.createBox(
      center,
      options,
      Quaternion.FromRotationMatrix(Matrix.LookDirectionLH(topPlaneNormal, surfacePlaneNormal)),
      'box',
    );
    box.rotate(Axis.X, Tools.ToRadians(90));
    return box;
  }

  private buildLayerFromBox(
    type: LAYER_TYPES.CUBOID | LAYER_TYPES.RECT_TUBE,
    box: Mesh,
    thickness?: number,
  ): Cuboid | RectTube {
    const calculatedData = this.volumeUtilsService.getCalculatedData(box);
    const layer = type === LAYER_TYPES.CUBOID ? new Cuboid() : new RectTube();
    layer.name = type === LAYER_TYPES.CUBOID ? LAYER_NAMES.CUBOID : LAYER_NAMES.RECT_TUBE;
    layer.isVisible = true;
    layer.isSaved = true;
    // setLayerMappingProps must run BEFORE calculateAzimuthAndVerticalTilt because
    // the latter calls bakeCurrentTransformIntoVertices(true) which resets the box's
    // rotation quaternion. setRotationQuaternion (inside setLayerMappingProps) needs
    // the original rotation.
    this.rectangleToolsUtilsService.setLayerMappingProps(layer, box, thickness);
    const [azimuth, verticalTilt] = this.calculateAzimuthAndVerticalTilt(box);
    this.rectangleToolsUtilsService.setLayerMetricProps(
      layer,
      calculatedData,
      azimuth,
      verticalTilt,
    );
    return layer;
  }
}
